import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { T } from "@/lib/testIds";
import Layout from "@/components/Layout";
import { ArrowRight, HeartHandshake, Coffee } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

export default function Landing() {
  const { user } = useAuth();
  const [stats, setStats] = useState({ courses: null, lessons: null, flashcards: null });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/public/stats");
        setStats(data);
      } catch {
        // Non-fatal: leave placeholders as null.
      }
    })();
  }, []);

  const fmt = (n) => (n == null ? "—" : n >= 100 ? `${n}+` : `${n}`);

  return (
    <Layout>
      <section className="grid lg:grid-cols-12 gap-10 items-center">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="lg:col-span-7">
          <div className="text-xs uppercase tracking-[0.28em] text-brand-muted mb-6">A training platform for UK general practice</div>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.02] text-brand-ink">
            Training that fits <br />
            round the <em className="italic text-brand-accent">day</em> —<br />
            not the other way round.
          </h1>
          <p className="mt-6 text-lg text-brand-body max-w-xl leading-relaxed">
            Between calls at the desk, on your phone with a cuppa at home, or any free
            moment in between. The lesson waits.
          </p>

          <div
            data-testid="landing-free-badge"
            className="mt-8 inline-flex items-center gap-2 bg-brand-accent/10 text-brand-accent border border-brand-accent/30 rounded-full px-4 py-1.5 text-sm font-medium"
          >
            <HeartHandshake className="w-4 h-4" strokeWidth={1.75} />
            100% free · no card · no tier · always
          </div>

          <div className="mt-10 flex flex-col sm:flex-row sm:flex-wrap gap-3 items-stretch sm:items-start">
            <Link to={user ? "/dashboard" : "/register"} data-testid={T.landingCta}
              className="inline-flex items-center justify-center gap-2 bg-brand-ink text-brand-bone rounded-full px-7 py-4 font-medium hover:-translate-y-0.5 hover:shadow-cardHover transition-all">
              Get started — free <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
            <Link to={user ? "/dashboard" : "/login"} data-testid={T.landingLogin}
              className="inline-flex items-center justify-center gap-2 border border-brand-linen text-brand-ink rounded-full px-7 py-4 font-medium hover:bg-brand-linen transition-colors">
              Log in
            </Link>
          </div>
          <div className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-6" data-testid="landing-stats">
            {[
              { n: fmt(stats.courses), l: "Courses" },
              { n: fmt(stats.lessons), l: "Lessons" },
              { n: fmt(stats.flashcards), l: "Flashcards" },
              { n: "UK", l: "Spellings" },
            ].map((s) => (
              <div key={s.l} data-testid={`landing-stat-${s.l.toLowerCase()}`}>
                <div className="font-serif text-4xl text-brand-ink">{s.n}</div>
                <div className="text-xs uppercase tracking-widest text-brand-muted mt-1">{s.l}</div>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.9, delay: 0.1 }} className="lg:col-span-5">
          <div className="relative aspect-[4/5] rounded-3xl overflow-hidden shadow-cardHover bg-brand-ink">
            <img
              src="https://images.unsplash.com/photo-1530026405186-ed1f139313f8?auto=format&fit=crop&w=1400&q=85"
              alt="Anatomical heart and brain models"
              className="absolute inset-0 w-full h-full object-cover"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-brand-ink via-brand-ink/40 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6 text-brand-bone">
              <div className="text-[11px] tracking-[0.25em] uppercase opacity-80">Featured course</div>
              <div className="font-serif text-3xl mt-2">Medical Terminology</div>
              <div className="text-sm opacity-90 mt-1">Foundation · UK spellings · ~5 hours</div>
            </div>
          </div>
        </motion.div>
      </section>

      <section
        data-testid="landing-free-section"
        className="mt-28 rounded-3xl bg-brand-ink text-brand-bone p-10 sm:p-16 relative overflow-hidden"
      >
        <div className="max-w-3xl">
          <div className="flex flex-wrap items-center gap-3">
            <HeartHandshake className="w-8 h-8 text-brand-accent" strokeWidth={1.5} />
            <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest bg-brand-bone/10 text-brand-bone border border-brand-bone/20 rounded-full px-3 py-1">
              Free forever · no card required
            </span>
          </div>
          <h2 className="font-serif text-4xl sm:text-5xl mt-6 text-brand-bone">
            The whole thing is <em className="italic">free</em>.
          </h2>
          <p className="mt-4 text-brand-linen/85 leading-relaxed text-lg">
            Every course. Every quiz. Every flashcard. Every certificate. For every
            person in the practice — receptionists, admin, HCAs, ANPs, GPs, PMs, locums,
            trainees. No trial, no free tier that runs out, no upsell tucked behind the
            next lesson. If it's on Calside, it's yours.
          </p>
          <ul className="mt-6 space-y-2 text-brand-linen/80 text-base">
            <li className="flex items-start gap-3">
              <span className="mt-2 w-1.5 h-1.5 rounded-full bg-brand-accent shrink-0" />
              <span>No credit card at sign-up. Not now. Not later.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-2 w-1.5 h-1.5 rounded-full bg-brand-accent shrink-0" />
              <span>Whole practice on one invite code — bring the whole team, no seat cap.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-2 w-1.5 h-1.5 rounded-full bg-brand-accent shrink-0" />
              <span>Certificates you can actually download and keep.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-2 w-1.5 h-1.5 rounded-full bg-brand-accent shrink-0" />
              <span>UK spellings, Scottish NHS depth — built for the day-job, not a syllabus.</span>
            </li>
          </ul>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link
              to={user ? "/dashboard" : "/register"}
              data-testid="landing-free-cta"
              className="inline-flex items-center gap-2 bg-brand-accent text-white rounded-full px-6 py-3 font-medium hover:bg-brand-accentHover transition-colors"
            >
              Start now — takes a minute <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
            <span className="inline-flex items-center gap-2 text-brand-linen/70 text-sm px-2">
              <Coffee className="w-4 h-4" strokeWidth={1.5} /> Or bookmark it for the cuppa break.
            </span>
          </div>
        </div>
      </section>
    </Layout>
  );
}
